/**
 * Demo Player Engine — drives an automated, narrated tour of the app.
 *
 * Script format (see ./scripts/*.js for examples):
 *
 *   export default {
 *     category: 'grocery',
 *     title: 'Run your grocery store on autopilot',
 *     initialRoute: '/huber-mart/grocery',
 *     steps: [
 *       { type: 'narrate', text: 'Welcome…', duration: 4000 },
 *       { type: 'point', target: '[data-testid="kpi-todays-sales-card"]',
 *         text: 'Up top you see today's sales…', duration: 5000 },
 *       { type: 'click', target: '[data-testid="some-tab"]',
 *         text: 'Tap here to view tax…', duration: 4000 },
 *       { type: 'navigate', url: '/.../grocery?tab=inventory', text: '…', duration: 3000 },
 *     ],
 *   }
 *
 * Adding a new category (e.g. roofing):
 *   1. Create scripts/roofing.js exporting the same shape
 *   2. Register it in scripts/index.js
 *   That's it — engine code unchanged.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Play, Pause, SkipForward, RotateCcw, Volume2, VolumeX, X, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import scripts from "./scripts";

// Smooth-ease the fake cursor between two points
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export default function DemoPlayer() {
  const { category } = useParams();
  const navigate = useNavigate();
  const script = scripts[category];

  // Pre-populate the demo user in localStorage so the iframe (same origin)
  // can render the dashboards that require auth.
  //
  // CRITICAL: this MUST happen synchronously BEFORE the iframe element is
  // first rendered, AND it must survive React StrictMode's synthetic
  // mount → unmount → remount cycle in dev. We use a module-level cache
  // for the prior snapshot (set once) plus a synchronous body write so
  // localStorage always reflects the demo user from the very first render.
  const STORAGE_KEY = "app_user";
  const TOKEN_KEY = "app_token";
  const demoUser = {
    grocery: {
      user_id: "demo-grocery", name: "Rachel Ortega",
      email: "demo+grocery@example.com", role: "owner",
      business_slug: "maple-market", business_name: "Maple Grove Market",
      business_category: "grocery_store",
    },
    wholesale: {
      user_id: "demo-wholesale", name: "Daniel Park",
      email: "demo+wholesale@example.com", role: "owner",
      business_slug: "atlas-trade", business_name: "Atlas Trade Imports, LLC",
      business_category: "wholesale",
    },
    mechanic: {
      user_id: "demo-mechanic", name: "Sam Bennett",
      email: "demo+mechanic@example.com", role: "owner",
      business_slug: "riverside-auto", business_name: "Riverside Auto Service, LLC",
      business_category: "truck_repair",
    },
  }[category];

  // Capture the visitor's real session ONCE before we overwrite it. Stored
  // on the window so StrictMode's double-mount can't lose it.
  if (typeof window !== "undefined" && window.__demoPrevSession === undefined) {
    window.__demoPrevSession = {
      user: localStorage.getItem(STORAGE_KEY),
      token: localStorage.getItem(TOKEN_KEY),
    };
  }

  // Always write the demo user on every render. Cheap, idempotent, and
  // bullet-proof against any code path that clears it.
  if (demoUser) {
    const desired = JSON.stringify(demoUser);
    if (localStorage.getItem(STORAGE_KEY) !== desired) {
      localStorage.setItem(STORAGE_KEY, desired);
    }
  }

  // Restore prior session only on REAL unmount (the visitor leaves the demo).
  // We schedule restore with a small delay; StrictMode's synthetic remount
  // arrives in microseconds and the new render's body write above will have
  // already re-set the demo user, so the visitor never sees a flash.
  useEffect(() => {
    return () => {
      setTimeout(() => {
        // If the demo page is no longer mounted, restore the visitor's session.
        if (!document.querySelector('[data-testid="demo-player"]')) {
          const prev = window.__demoPrevSession || { user: null, token: null };
          if (prev.user) localStorage.setItem(STORAGE_KEY, prev.user);
          else localStorage.removeItem(STORAGE_KEY);
          if (prev.token) localStorage.setItem(TOKEN_KEY, prev.token);
          else localStorage.removeItem(TOKEN_KEY);
          delete window.__demoPrevSession;
        }
      }, 100);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [stepIndex, setStepIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [voicesReady, setVoicesReady] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [highlight, setHighlight] = useState(null); // {x,y,w,h}
  const [bubbleText, setBubbleText] = useState("");
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const iframeRef = useRef(null);
  const cancelRef = useRef({ cancelled: false });
  const speechRef = useRef(null);

  // Resolve a target selector inside the iframe → return its viewport rect (relative to iframe)
  const findRect = useCallback((selector) => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) return null;
      const el = doc.querySelector(selector);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      // Scroll element into view if off-screen — use 'instant' (not 'smooth')
      // because we measure the rect again on the very next line and the demo
      // can't wait for a 300ms smooth scroll to land before highlighting/typing.
      if (r.top < 0 || r.bottom > doc.defaultView.innerHeight) {
        el.scrollIntoView({ behavior: "instant", block: "center" });
      }
      const r2 = el.getBoundingClientRect();
      return { x: r2.left, y: r2.top, w: r2.width, h: r2.height, el };
    } catch (e) {
      return null;
    }
  }, []);

  // Animate the fake cursor from current pos → target pos over `duration` ms
  const moveCursor = useCallback((to, duration = 900) => {
    return new Promise((resolve) => {
      const start = performance.now();
      const from = cursorPos;
      const tick = (now) => {
        if (cancelRef.current.cancelled) return resolve();
        const t = Math.min(1, (now - start) / duration);
        const e = easeInOutCubic(t);
        setCursorPos({
          x: from.x + (to.x - from.x) * e,
          y: from.y + (to.y - from.y) * e,
        });
        if (t < 1) requestAnimationFrame(tick);
        else resolve();
      };
      requestAnimationFrame(tick);
    });
  }, [cursorPos]);

  // Speak text via Web Speech API. Uses the best English voice available.
  // Returns a Promise that resolves when speech ends (or immediately if muted).
  const speak = useCallback((text) => {
    return new Promise((resolve) => {
      if (!text) return resolve();
      if (muted || !("speechSynthesis" in window)) {
        setBubbleText(text);
        return resolve();
      }
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.02;
      u.pitch = 1.0;
      u.volume = 0.95;
      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find(
        (v) =>
          (v.name.includes("Samantha") || v.name.includes("Google US English") || v.name.includes("Aria") || v.name.includes("Jenny")) &&
          v.lang.startsWith("en")
      ) || voices.find((v) => v.lang === "en-US") || voices[0];
      if (preferred) u.voice = preferred;
      u.onend = () => resolve();
      u.onerror = () => resolve();
      speechRef.current = u;
      setBubbleText(text);
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    });
  }, [muted]);

  // Wait helper that respects cancel
  const sleep = (ms) =>
    new Promise((resolve) => {
      const id = setTimeout(() => resolve(), ms);
      // Note: cancel handled by cancelRef polling on each major step
      return id;
    });

  // ---------------------------------------------------------------------------
  // MASTER CLOCK SCHEDULER
  //
  // Every step has a precomputed start offset (cumulative `duration` of all
  // prior steps, plus a 1.5s lead-in for the iframe to fully hydrate). All
  // visual actions are scheduled with setTimeout against this master clock,
  // so the on-screen cursor + spotlight always lands BEFORE the matching
  // audio line plays. This kills the drift between voice and visuals.
  //
  // Audio is muxed in post (record_voiced_demos.py) using the SAME offsets,
  // so video and audio line up to within ~50ms.
  // ---------------------------------------------------------------------------
  const LEAD_IN_MS = 3500;        // dashboard hydration grace before step 1
  const CURSOR_LEAD_MS = 700;   // cursor starts moving 700ms before audio
  const NAVIGATE_LEAD_MS = 1800; // iframe.src changes 1.8s BEFORE audio fires so
                                 // the new page is fully painted by the time the
                                 // narration starts describing it — otherwise
                                 // the viewer hears "Tax reports…" while still
                                 // staring at the previous page (Payments)
                                 // because the iframe is still loading.

  // Try to load the audio-derived timings the recorder publishes alongside
  // the MP4 (e.g. /demos/grocery.timings.json). When present, these contain
  // the EXACT offsets and audio durations measured from the TTS output —
  // which is the only way visuals can match the voice without drift. Falls
  // back to the script's manually-set `duration` field if the file is missing.
  const [serverTimings, setServerTimings] = useState(null);
  const [timingsReady, setTimingsReady] = useState(false);
  useEffect(() => {
    if (!category) return;
    let abort = false;
    const done = () => { if (!abort) setTimingsReady(true); };
    fetch(`/demos/${category}.timings.json`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!abort && data?.steps?.length) setServerTimings(data); })
      .catch(() => {})
      .finally(done);
    // Safety: don't wait forever — fall through after 1.5s
    const id = setTimeout(done, 1500);
    return () => { abort = true; clearTimeout(id); };
  }, [category]);

  const stepStarts = (() => {
    if (!script) return [];
    // 1) Prefer server timings (audio-accurate)
    if (serverTimings?.steps?.length === script.steps.length) {
      return serverTimings.steps.map((s) => s.start_ms);
    }
    // 2) Fallback to script-declared durations
    const out = [];
    let t = LEAD_IN_MS;
    for (const step of script.steps) {
      out.push(t);
      t += step.duration || 3000;
    }
    return out;
  })();
  const totalMs = (() => {
    if (!script) return 0;
    if (serverTimings?.total_ms) return serverTimings.total_ms;
    return stepStarts.length
      ? stepStarts[stepStarts.length - 1] + (script.steps[script.steps.length - 1].duration || 3000)
      : 0;
  })();

  // Main run loop — master clock scheduler
  useEffect(() => {
    cancelRef.current.cancelled = false;
    if (!script || !playing || !timingsReady) return;

    // Signal to the external recorder that the demo is starting NOW. The
    // recorder uses this marker to trim its leading page-load segment so
    // muxed audio and recorded video stay perfectly aligned. Harmless when
    // not being recorded.
    if (typeof window !== "undefined") {
      window.__demoFirstStepAt = performance.now();
    }

    const timers = [];
    const schedule = (delay, fn) => {
      const id = setTimeout(() => {
        if (cancelRef.current.cancelled) return;
        fn();
      }, Math.max(0, delay));
      timers.push(id);
    };

    script.steps.forEach((step, i) => {
      const at = stepStarts[i];

      // 1) For NAVIGATE steps, change iframe src early so the page is
      //    fully hydrated by the time we start narrating.
      if (step.type === "navigate" && step.url) {
        schedule(at - NAVIGATE_LEAD_MS, () => {
          if (!iframeRef.current) return;
          setIframeLoaded(false);
          setHighlight(null);
          // Park cursor in a neutral spot off the active content area so it
          // doesn't end up sitting in some random input on the new page.
          const ifr = iframeRef.current.getBoundingClientRect();
          setCursorPos({ x: ifr.left + ifr.width / 2, y: ifr.top + 24 });
          iframeRef.current.src = step.url;
        });
      }

      // 2) For POINT/CLICK steps, start moving the cursor early so it
      //    arrives at the target right when the audio line starts.
      if (step.target && (step.type === "point" || step.type === "click")) {
        schedule(at - CURSOR_LEAD_MS, () => {
          // Quick non-blocking attempt to find the target. If it isn't
          // ready yet, retry every 150ms for up to 600ms — but never
          // block the master clock.
          let tries = 0;
          const tryFind = () => {
            const rect = findRect(step.target);
            if (rect && iframeRef.current) {
              const ifr = iframeRef.current.getBoundingClientRect();
              moveCursor(
                { x: ifr.left + rect.x + rect.w / 2, y: ifr.top + rect.y + rect.h / 2 },
                CURSOR_LEAD_MS - 50
              );
            } else if (tries++ < 4) {
              setTimeout(tryFind, 150);
            }
          };
          tryFind();
        });
      }

      // 3) At the step's exact "at" moment: show spotlight, fire click,
      //    advance the bubble. (Audio is muxed in post; the bubble doubles
      //    as live captions when watched in-browser.)
      schedule(at, () => {
        setStepIndex(i);

        // TYPE step: simulate a user typing into an input. We fire native
        // input events so React state updates correctly.
        if (step.type === "type" && step.target) {
          const rect = findRect(step.target);
          if (rect?.el && iframeRef.current) {
            const ifr = iframeRef.current.getBoundingClientRect();
            setHighlight({ x: ifr.left + rect.x, y: ifr.top + rect.y, w: rect.w, h: rect.h });
            setCursorPos({ x: ifr.left + rect.x + rect.w / 2, y: ifr.top + rect.y + rect.h / 2 });
            const el = rect.el.matches?.("input,textarea") ? rect.el : rect.el.querySelector?.("input,textarea") || rect.el;
            try {
              el.focus();
              const value = step.value || "";
              // Type one character at a time so the user can SEE it being typed.
              let idx = 0;
              const cps = Math.max(8, Math.min(20, value.length / Math.max(1, (step.duration || 3000) / 1000)));
              const interval = 1000 / cps;
              const doc = el.ownerDocument || document;
              const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set
                || Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set;
              const tick = () => {
                if (cancelRef.current.cancelled) return;
                idx += 1;
                const next = value.slice(0, idx);
                if (nativeSetter) nativeSetter.call(el, next);
                else el.value = next;
                el.dispatchEvent(new doc.defaultView.Event("input", { bubbles: true }));
                if (idx < value.length) {
                  const id = setTimeout(tick, interval);
                  timers.push(id);
                }
              };
              tick();
            } catch (e) { /* ignore */ }
          }
        } else if (step.type === "scroll" && iframeRef.current) {
          // SCROLL step: smoothly scroll the iframe body by `delta` px so the
          // recording captures the parts of the dashboard below the fold.
          setHighlight(null);
          try {
            const win = iframeRef.current.contentWindow;
            const delta = typeof step.delta === "number" ? step.delta : 500;
            win?.scrollBy({ top: delta, behavior: "smooth" });
          } catch (e) { /* cross-origin or not ready, ignore */ }
        } else if (step.type === "selectOption" && step.target) {
          // SELECT-OPTION step: changes the value of a shadcn/Radix <Select>.
          // Radix listboxes are notoriously flaky to drive from fake pointer
          // events — sometimes the click fires before the listbox mounts, or
          // the option's pointerdown is intercepted by Radix's own focus mgmt.
          // We do BOTH (a) visual click-the-trigger-then-click-the-option
          // for the recording, AND (b) call the page's exposed
          // `__demoSetItemType` setter as a guaranteed state-change fallback.
          // The setter wins — visuals are decorative.
          const rect = findRect(step.target);
          if (rect?.el && iframeRef.current) {
            const ifr = iframeRef.current.getBoundingClientRect();
            setHighlight({ x: ifr.left + rect.x, y: ifr.top + rect.y, w: rect.w, h: rect.h });
            setCursorPos({ x: ifr.left + rect.x + rect.w / 2, y: ifr.top + rect.y + rect.h / 2 });
            const win = iframeRef.current.contentWindow;
            // 1) Guaranteed state change via the React setter exposed by the page.
            const want = String(step.optionText || "").trim();
            try {
              if (win && typeof win.__demoSetItemType === "function") {
                win.__demoSetItemType(want.toLowerCase());
              }
            } catch (e) { /* ignore */ }
            // 2) Visual: open the Radix trigger then click the matching option
            //    so the recording SHOWS the dropdown open and selection happen.
            try {
              const trigger = rect.el.matches?.('[role="combobox"], button') ? rect.el : (rect.el.querySelector?.('[role="combobox"], button') || rect.el);
              const doc = trigger.ownerDocument || document;
              const evtOpts = { bubbles: true, cancelable: true, button: 0, pointerType: "mouse" };
              trigger.dispatchEvent(new doc.defaultView.PointerEvent("pointerdown", evtOpts));
              trigger.dispatchEvent(new doc.defaultView.PointerEvent("pointerup", evtOpts));
              trigger.dispatchEvent(new doc.defaultView.MouseEvent("click", evtOpts));
              let attempts = 0;
              const pickOption = () => {
                if (cancelRef.current.cancelled) return;
                const options = doc.querySelectorAll('[role="option"]');
                const wantLow = want.toLowerCase();
                const match = Array.from(options).find(o => (o.textContent || "").trim().toLowerCase() === wantLow);
                if (match) {
                  match.dispatchEvent(new doc.defaultView.PointerEvent("pointerdown", evtOpts));
                  match.dispatchEvent(new doc.defaultView.PointerEvent("pointerup", evtOpts));
                  match.dispatchEvent(new doc.defaultView.MouseEvent("click", evtOpts));
                } else if (attempts++ < 10) {
                  const id = setTimeout(pickOption, 80);
                  timers.push(id);
                }
              };
              const id = setTimeout(pickOption, 120);
              timers.push(id);
            } catch (e) { /* ignore */ }
          }
        } else if (step.target) {
          const rect = findRect(step.target);
          if (rect && iframeRef.current) {
            const ifr = iframeRef.current.getBoundingClientRect();
            setHighlight({
              x: ifr.left + rect.x,
              y: ifr.top + rect.y,
              w: rect.w,
              h: rect.h,
            });
            // Make sure cursor is sitting on the target even if the
            // pre-move missed it (target appeared late).
            setCursorPos({
              x: ifr.left + rect.x + rect.w / 2,
              y: ifr.top + rect.y + rect.h / 2,
            });
            if (step.type === "click") {
              try { rect.el?.click(); } catch (e) { /* ignore */ }
            }
          }
        } else if (step.type !== "navigate") {
          // pure narration: clear spotlight so the dashboard is fully visible
          setHighlight(null);
        }

        if (step.text) {
          setBubbleText(step.text);
          // Web Speech for live in-browser preview only; in the recorded
          // MP4 this is silent and we mux OpenAI TTS on top.
          speak(step.text);
        }
      });
    });

    // End of demo
    schedule(totalMs + 400, () => {
      setPlaying(false);
      setHighlight(null);
      setBubbleText("That's the tour. Ready to set up your own store?");
    });

    return () => {
      cancelRef.current.cancelled = true;
      timers.forEach((id) => clearTimeout(id));
      window.speechSynthesis?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, script, timingsReady]);

  // Detect voices ready (some browsers load async)
  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    const check = () => setVoicesReady(window.speechSynthesis.getVoices().length > 0);
    check();
    window.speechSynthesis.onvoiceschanged = check;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const handlePauseToggle = () => {
    if (playing) {
      window.speechSynthesis?.pause();
      cancelRef.current.cancelled = true;
    } else {
      window.speechSynthesis?.resume();
    }
    setPlaying((p) => !p);
  };

  const handleSkip = () => {
    window.speechSynthesis?.cancel();
    cancelRef.current.cancelled = true;
    setTimeout(() => {
      setStepIndex((i) => Math.min(i + 1, (script?.steps.length || 1) - 1));
      cancelRef.current.cancelled = false;
      setPlaying(true);
    }, 50);
  };

  const handleRestart = () => {
    window.speechSynthesis?.cancel();
    cancelRef.current.cancelled = true;
    setTimeout(() => {
      setStepIndex(0);
      cancelRef.current.cancelled = false;
      setPlaying(true);
      if (iframeRef.current && script?.initialRoute) {
        iframeRef.current.src = script.initialRoute;
      }
    }, 50);
  };

  if (!script) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold">Demo not found</h1>
          <p className="text-slate-400">No demo script for category "{category}".</p>
          <Link to="/demos"><Button>Back to demos</Button></Link>
        </div>
      </div>
    );
  }

  const progress = ((stepIndex + 1) / script.steps.length) * 100;

  return (
    <div className="h-screen bg-slate-950 text-slate-100 flex flex-col overflow-hidden" data-testid="demo-player">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-800 bg-slate-900/95 backdrop-blur z-50">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/welcome" className="text-slate-400 hover:text-white shrink-0"><X className="w-5 h-5" /></Link>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-semibold">Interactive demo · {script.category}</p>
            <h1 className="text-sm font-semibold truncate">{script.title}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-400 hidden sm:inline">Step {stepIndex + 1} / {script.steps.length}</span>
          <Button size="sm" variant="ghost" onClick={() => setMuted((m) => !m)} className="text-slate-300 hover:text-white" data-testid="demo-mute-btn">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={handlePauseToggle} className="text-slate-300 hover:text-white" data-testid="demo-pause-btn">
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={handleSkip} className="text-slate-300 hover:text-white" data-testid="demo-skip-btn">
            <SkipForward className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={handleRestart} className="text-slate-300 hover:text-white" data-testid="demo-restart-btn">
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Link to="/signup">
            <Button size="sm" className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white">Start free</Button>
          </Link>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-0.5 bg-slate-800">
        <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all" style={{ width: `${progress}%` }} />
      </div>

      {/* Iframe + overlay */}
      <div className="relative flex-1 overflow-hidden">
        <iframe
          ref={iframeRef}
          src={script.initialRoute}
          title={`Demo · ${script.category}`}
          className="w-full h-full border-0"
          sandbox="allow-same-origin allow-scripts allow-forms"
          onLoad={() => setIframeLoaded(true)}
        />
        {!iframeLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-950/70">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
          </div>
        )}

        {/* Spotlight ring around target */}
        {highlight && (
          <div
            className="pointer-events-none fixed rounded-xl transition-all duration-500 z-40"
            style={{
              left: highlight.x - 6,
              top: highlight.y - 6,
              width: highlight.w + 12,
              height: highlight.h + 12,
              boxShadow: "0 0 0 3px rgba(34, 211, 238, 0.7), 0 0 0 9999px rgba(2, 6, 23, 0.18)",
            }}
          />
        )}

        {/* Animated fake cursor */}
        <div
          className="pointer-events-none fixed z-50 transition-transform"
          style={{ transform: `translate(${cursorPos.x}px, ${cursorPos.y}px)`, left: -12, top: -10 }}
        >
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <path d="M5 3 L23 14 L14 16 L11 24 Z" fill="#fff" stroke="#0f172a" strokeWidth="1.5" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Narration bubble — bottom center */}
        {bubbleText && (
          <div className="pointer-events-none absolute inset-x-4 bottom-6 z-40 flex justify-center">
            <div className="max-w-2xl bg-slate-900/95 backdrop-blur border border-cyan-500/30 rounded-2xl px-5 py-3 shadow-2xl">
              <p className="text-[13px] sm:text-sm leading-relaxed text-slate-100">{bubbleText}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
